import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { PatchManagementSectionState } from './customStates/patchManagement/patch-management.types';
import { NavigationHeaderSlice } from './navigationHeaderSlice/types.navigation';
import { SaveButtonSlice } from './saveButtonSlice/types.save';
import { AccordionsStatusSlice } from './statusSlice/types.status';

export interface SectionState {
  isValid: boolean;
  masks?: SectionMask[];
  status: SectionStatus;
}

export interface SectionStatus {
  type: SectionStatusType;
  progress?: {
    count: number;
    total: number;
  };
}

export enum DataLoadedStatus {
  NOT_LOADED = 0,
  SUCCESS = 1,
  ERROR = 2,
}

export enum SectionStatusType {
  NONE = 0,
  OFF,
  ON,
  PARTIAL,
  WARNING,
}

// IMPORTANT! Add new mask in order of priority. The first mask will be the one that is shown in the UI.
export enum SectionMask {
  DEFAULT = 0,
  INHERITANCE = 1,
  LICENSE_RESTRICTION = 2,
  ANTIMALWARE_ON_ACCESS = 3,
  NETWORK_PROTECTION_TRAFFIC = 4,
}

export interface SandboxModuleSectionsState {
  [SectionId.ENDPOINT_SENSOR]: SectionState;
}
export interface PatchManagementModuleSectionsState {
  [SectionId.NONE]: PatchManagementSectionState;
}
export interface IntegrityMonitorModuleSectionsState {
  [SectionId.INTEGRITY_MONITOR_REAL_TIME]: SectionState;
}
export interface EdrSensorModuleSectionsState {
  [SectionId.GENERAL]: SectionState;
}
export interface EncryptionModuleSectionsState {
  [SectionId.GENERAL]: SectionState;
}
export interface LiveSearchModuleSectionsState {
  [SectionId.NONE]: SectionState;
}
export interface NsxModuleSectionsState {
  [SectionId.NONE]: SectionState;
}
export interface StorageProtectionModuleSectionsState {
  [SectionId.STORAGE_PROTECTION_ICAP]: SectionState;
  [SectionId.EXCLUSIONS]: SectionState;
}
export interface IndicatorsOfRiskModuleSectionsState {
  [SectionId.NONE]: SectionState;
}

export interface DeviceControlModuleSectionsState {
  [SectionId.RULES]: SectionState;
  [SectionId.EXCLUSIONS]: SectionState;
}
export interface ApplicationControlModuleSectionsState {
  [SectionId.NONE]: SectionState;
}

export interface GeneralModuleSectionsState {
  [SectionId.SECURITY_TELEMETRY]: SectionState;
  [SectionId.COMMUNICATION]: SectionState;
  [SectionId.GENERAL_NOTIFICATIONS]: SectionState;
  [SectionId.SETTINGS]: SectionState;
}

export interface PolicySettingsModuleSectionsState {
  [SectionId.GENERAL_INHERITANCE]: SectionState;
  [SectionId.DETAILS]: SectionState;
}

export type ModuleSectionsState =
  | SandboxModuleSectionsState
  | PatchManagementModuleSectionsState
  | IntegrityMonitorModuleSectionsState
  | EdrSensorModuleSectionsState
  | EncryptionModuleSectionsState
  | StorageProtectionModuleSectionsState
  | IndicatorsOfRiskModuleSectionsState
  | DeviceControlModuleSectionsState
  | LiveSearchModuleSectionsState
  | ApplicationControlModuleSectionsState
  | NsxModuleSectionsState
  | GeneralModuleSectionsState
  | PolicySettingsModuleSectionsState;

export interface ModuleState {
  sections: ModuleSectionsState;
}

export interface PolicyAppStateModules {
  [ModuleId.SANDBOX]?: ModuleState;
  [ModuleId.PATCH_MANAGEMENT]?: ModuleState;
  [ModuleId.INTEGRITY_MONITOR]?: ModuleState;
  [ModuleId.EDR_SENSOR]?: ModuleState;
  [ModuleId.ENCRYPTION]?: ModuleState;
  [ModuleId.STORAGE_PROTECTION]?: ModuleState;
  [ModuleId.INDICATORS_OF_RISK]?: ModuleState;
  [ModuleId.DEVICE_CONTROL]?: ModuleState;
  [ModuleId.LIVE_SEARCH]?: ModuleState;
  [ModuleId.APPLICATION_CONTROL]?: ModuleState;
  [ModuleId.NSX]?: ModuleState;
  [ModuleId.BLOCKLIST_RULES]?: ModuleState;
  [ModuleId.GENERAL]?: ModuleState;
  [ModuleId.POLICY_SETTINGS]?: ModuleState;
}

export interface PolicyAppState {
  navigationHeaderSlice: NavigationHeaderSlice;
  phantom: boolean;
  modules: PolicyAppStateModules;
  isPolicyDataLoaded: boolean;
  saveButtonSlice: SaveButtonSlice;
  accordionsSlice: {
    status: AccordionsStatusSlice;
  };
}
